import { createServerFn } from "@tanstack/react-start";
import { redirect } from "@tanstack/react-router";
import { Result } from "better-result";
import { z } from "zod/v4";
import {
  impersonateUser,
  stopImpersonateUser,
} from "#/modules/util.impersonate/api.impersonate.server";
import { isUserImpersonating } from "#/modules/util.impersonate/utils.impersonate.server";
import { getAuthSession } from "#/modules/util.auth/session.auth.server";
import { adminMiddleware } from "#/modules/util.auth/middleware.admin";
import { authMiddleware } from "#/modules/util.auth/middleware.auth";
import { toastErrorMiddleware } from "#/modules/util.errors/middleware.toast-errors";
import { apiFailure } from "#/modules/util.errors/api-failure.server";

/**
 * Start impersonating a user. Swaps the session's access token for the impersonation token
 * (cookie write happens here, in a POST server fn) and redirects to the app root, where the
 * now-impersonated user is routed to their school. Mirrors portal's `api.impersonate` action.
 */
export const startImpersonation = createServerFn({ method: "POST" })
  .middleware([adminMiddleware, toastErrorMiddleware])
  .validator(z.object({ targetUserId: z.string().min(1), locale: z.string().min(1) }))
  .handler(async ({ data }) => {
    const session = await getAuthSession();
    const tokens = session.data.tokens;
    if (!tokens) {
      throw new Error("No tokens found in admin session while impersonating");
    }

    const result = await impersonateUser({
      targetUserId: data.targetUserId,
    });
    if (Result.isError(result)) return apiFailure(result.error);
    const impersonationResponse = result.value;
    if (!impersonationResponse) throw new Error("No impersonation response received");

    await session.update({
      tokens: { ...tokens, accessToken: impersonationResponse.impersonationToken },
      // A lock left over from the admin's own les points at a school the impersonated docent has no
      // membership on — the $school guard would redirect there and TenantMiddleware would 403.
      lockedLes: null,
    });

    throw redirect({
      to: "/$locale/{-$schoolyear}",
      params: { locale: data.locale },
    });
  });

/**
 * Stop impersonating. Restores the admin's original access token (cookie write here) and
 * redirects back to the admin users overview. Mirrors portal's `api.stop-impersonate` action.
 *
 * Guarded by {@link authMiddleware} (a valid session), NOT {@link adminMiddleware}: while
 * impersonating, the session access token IS the impersonation token, which carries the TARGET
 * user's roles (e.g. Docent), not Admin — so an admin-role check would reject the caller and leave
 * them stuck impersonating. This matches the RR7 portal's `_protected.admin.api.stop-impersonate`
 * action, which has no admin guard. Stopping is not privilege-escalating: the backend resolves the
 * impersonation token's `OriginalUserId` claim (forwarded as the Bearer token by the fetch-client's
 * auth middleware) and hands back the original admin session it already issued.
 */
export const stopImpersonation = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(z.object({ locale: z.string().min(1) }))
  .handler(async ({ data }) => {
    const toAdminUsers = redirect({
      to: "/$locale/{-$schoolyear}/admin/users",
      params: { locale: data.locale },
    });

    // The session cookie is shared by every tab: once impersonation ended elsewhere, this tab's stale
    // banner still offers "stop", and the backend rejects the admin token with 400 UserNotFound.
    if (!(await isUserImpersonating())) throw toAdminUsers;

    const session = await getAuthSession();
    const tokens = session.data.tokens;
    if (!tokens) {
      throw new Error("No tokens found in admin session while stopping impersonation");
    }

    const result = await stopImpersonateUser();
    if (Result.isError(result)) return apiFailure(result.error);
    const originalToken = result.value;

    await session.update({
      tokens: { ...tokens, accessToken: originalToken.accessToken },
      // The lesson lock belongs to the impersonated docent's session, not the admin's own — leaving
      // it set traps the admin in that docent's student view with only a password unlock.
      lockedLes: null,
    });

    throw toAdminUsers;
  });
